import { Button } from "@istari-frontend/ui";
import { SquarePen } from "lucide-react";
import { useId } from "react";

import type { Conversation } from "~/ai-chat/model/conversation";
import { cn } from "~/utils/cn";

interface ConversationListProps {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  isLoading?: boolean;
}

const UNTITLED_LABEL = "Untitled chat";

export function ConversationList({ conversations, activeId, onSelect, onNew, isLoading }: ConversationListProps) {
  const headingId = useId();

  return (
    <nav aria-labelledby={headingId} className="bg-card flex h-full flex-col gap-2 p-2">
      <h2 id={headingId} className="sr-only">
        Conversations
      </h2>
      <Button type="button" variant="outline" size="sm" onClick={onNew} className="justify-start">
        <SquarePen />
        New conversation
      </Button>

      {isLoading ? (
        <p className="text-muted-foreground px-3 py-2 text-sm">Loading conversations…</p>
      ) : conversations.length === 0 ? (
        <p className="text-muted-foreground px-3 py-2 text-sm">No conversations yet.</p>
      ) : (
        <ul className="flex flex-col gap-0.5 overflow-y-auto">
          {conversations.map((conversation) => {
            const isActive = conversation.id === activeId;
            return (
              <li key={conversation.id}>
                <button
                  type="button"
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => onSelect(conversation.id)}
                  className={cn(
                    "w-full truncate rounded-sm px-3 py-2 text-left text-sm transition-colors outline-none",
                    "hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                    isActive && "bg-accent text-accent-foreground font-medium",
                  )}
                >
                  {conversation.title ?? UNTITLED_LABEL}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
}
